<template>
  <div class="home general">
    <h1 class="section-title">Usuarios y reproducciones</h1>

    <div class="filters">
      <va-input v-model="since" type="date" label="Desde" />
      <va-input v-model="until" type="date" label="Hasta" />
      <va-button color="info" :disabled="isLoading" @click="loadReport">
        Consultar
      </va-button>
    </div>

    <p v-if="error" class="analytics-error">{{ error }}</p>
    <p v-if="isLoading" class="analytics-status">Cargando actividad...</p>

    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Reproducciones</span>
        <strong>{{ report.summary.plays }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios activos</span>
        <strong>{{ report.summary.active_users }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios nuevos</span>
        <strong>{{ report.summary.new_users }}</strong>
      </div>
      <div class="summary-item">
        <span>Ventas efectivas</span>
        <strong>{{ report.summary.effective_sales }}</strong>
      </div>
      <div class="summary-item">
        <span>USD ventas</span>
        <strong>{{ formatUsd(report.summary.sales_usd) }}</strong>
      </div>
      <div class="summary-item">
        <span>Reembolsos</span>
        <strong>{{ report.summary.refunds }}</strong>
      </div>
      <div class="summary-item">
        <span>USD reembolsos</span>
        <strong>{{ formatUsd(report.summary.refunds_usd) }}</strong>
      </div>
      <div class="summary-item">
        <span>USD neto</span>
        <strong>{{ formatUsd(report.summary.net_usd) }}</strong>
      </div>
      <div class="summary-item">
        <span>Canciones distintas</span>
        <strong>{{ report.summary.unique_songs }}</strong>
      </div>
      <div class="summary-item">
        <span>Días con actividad</span>
        <strong>{{ report.summary.active_days }}</strong>
      </div>
      <div class="summary-item">
        <span>Promedio diario</span>
        <strong>{{ formatNumber(report.summary.avg_plays_per_day) }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios por día</span>
        <strong>{{ formatNumber(report.summary.avg_active_users_per_day) }}</strong>
      </div>
    </div>

    <h2 class="section-title">Por mes</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.by_month : []"
      :columns="monthColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(plays_per_user)="{ rowData }">
        {{ formatNumber(rowData.plays_per_user) }}
      </template>
      <template #cell(sales_usd)="{ rowData }">
        {{ formatUsd(rowData.sales_usd) }}
      </template>
      <template #cell(refunds_usd)="{ rowData }">
        {{ formatUsd(rowData.refunds_usd) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Por día</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.by_day : []"
      :columns="dayColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(plays_per_user)="{ rowData }">
        {{ formatNumber(rowData.plays_per_user) }}
      </template>
      <template #cell(sales_usd)="{ rowData }">
        {{ formatUsd(rowData.sales_usd) }}
      </template>
      <template #cell(refunds_usd)="{ rowData }">
        {{ formatUsd(rowData.refunds_usd) }}
      </template>
    </va-data-table>
  </div>
</template>

<script>
import requester from "@/services/requester";

function currentMonthRange() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0");
  return {
    since: `${now.getFullYear()}-${month}-01`,
    until: `${now.getFullYear()}-${month}-${lastDay}`,
  };
}

export default {
  name: "PlaybackActivity",
  data() {
    const range = currentMonthRange();
    return {
      since: range.since,
      until: range.until,
      isLoading: false,
      error: "",
      report: null,
      monthColumns: [
        { key: "month_label", sortable: false, label: "Mes" },
        { key: "active_users", sortable: false, label: "Usuarios activos" },
        { key: "new_users", sortable: false, label: "Usuarios nuevos" },
        { key: "effective_sales", sortable: false, label: "Ventas efectivas" },
        { key: "sales_usd", sortable: false, label: "USD ventas" },
        { key: "refunds", sortable: false, label: "Reembolsos" },
        { key: "refunds_usd", sortable: false, label: "USD reembolsos" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_songs", sortable: false, label: "Canciones distintas" },
        { key: "plays_per_user", sortable: false, label: "Repros. por usuario" },
      ],
      dayColumns: [
        { key: "day_label", sortable: false, label: "Día" },
        { key: "active_users", sortable: false, label: "Usuarios activos" },
        { key: "new_users", sortable: false, label: "Usuarios nuevos" },
        { key: "effective_sales", sortable: false, label: "Ventas efectivas" },
        { key: "sales_usd", sortable: false, label: "USD ventas" },
        { key: "refunds", sortable: false, label: "Reembolsos" },
        { key: "refunds_usd", sortable: false, label: "USD reembolsos" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_songs", sortable: false, label: "Canciones distintas" },
        { key: "plays_per_user", sortable: false, label: "Repros. por usuario" },
      ],
    };
  },
  methods: {
    formatUsd(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
    formatNumber(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value).toFixed(1);
    },
    async loadReport() {
      this.isLoading = true;
      this.error = "";
      try {
        const query = [];
        if (this.since) {
          query.push(`since=${this.since}`);
        }
        if (this.until) {
          query.push(`until=${this.until}`);
        }
        const route = query.length ? `/activity?${query.join("&")}` : "/activity";
        const data = await requester.Get(route, true);
        if (data.status === 200 && data.data) {
          this.report = data.data;
        } else {
          this.error = "No se pudo cargar la actividad.";
        }
      } catch (e) {
        console.log(e);
        this.error = "No se pudo cargar la actividad.";
      }
      this.isLoading = false;
    },
  },
  created() {
    this.loadReport();
  },
};
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary-item {
  background: #123d62;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.analytics-error,
.analytics-status {
  padding: 0 15px 16px;
}

.analytics-error {
  color: #f8b4b4;
}
</style>
